import React, { useState } from 'react';
import { User, ChevronDown, ChevronUp } from 'lucide-react';
import { ref, update } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { updateEmail, updatePassword, updateProfile } from 'firebase/auth';
import { database, storage } from '../../firebase/config';
import DragDropZone from '../DragDropZone';

export default function UserProfile({ user, bandId, bandData, setError, showMessage, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const updateDisplayName = async () => {
    if (!newDisplayName.trim() || !user || !bandId) {
      setError('Please enter a display name');
      return;
    }
    try {
      await updateProfile(user, { displayName: newDisplayName });
      await update(ref(database, `bands/${bandId}/members/${user.uid}`), { displayName: newDisplayName });
      setNewDisplayName('');
      showMessage('Display name updated successfully!');
    } catch (error) {
      setError('Failed to update display name: ' + error.message);
    }
  };

  const updateUserEmail = async () => {
    if (!newEmail.trim() || !user) return;
    try {
      await updateEmail(user, newEmail);
      await update(ref(database, `bands/${bandId}/members/${user.uid}`), { email: newEmail });
      setNewEmail('');
      showMessage('Email updated successfully!');
    } catch (error) {
      setError('Failed to update email: ' + error.message);
    }
  };

  const updateUserPassword = async () => {
    if (!newPassword || !confirmPassword || !user) {
      setError('Please fill in both password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await updatePassword(user, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      showMessage('Password updated successfully!');
    } catch (error) {
      setError('Failed to update password: ' + error.message);
    }
  };

  const updateAvatar = async (file) => {
    if (!file || !user || !bandId) return;
    try {
      setUploadingAvatar(true);
      setError('');

      const timestamp = Date.now();
      const filename = `bands/${bandId}/avatars/${user.uid}_${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);
      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on('state_changed',
        () => {},
        (error) => {
          setError('Avatar upload failed: ' + error.message);
          setUploadingAvatar(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const currentMember = bandData?.members?.[user.uid];
            if (currentMember?.avatarPath) {
              try {
                const oldFileRef = storageRef(storage, currentMember.avatarPath);
                await deleteObject(oldFileRef);
              } catch (err) {
                console.log('Old avatar not found or already deleted');
              }
            }
            await update(ref(database, `bands/${bandId}/members/${user.uid}`), {
              avatar: downloadURL,
              avatarPath: filename
            });
            setUploadingAvatar(false);
            showMessage('Avatar updated successfully!');
          } catch (err) {
            setError('Failed to save avatar: ' + err.message);
            setUploadingAvatar(false);
          }
        }
      );
    } catch (error) {
      setError('Failed to upload avatar: ' + error.message);
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-white" />
          Your Profile
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <div className="space-y-6">
            {/* Avatar */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Profile Avatar</label>
              <div className="flex items-center gap-4">
                {bandData?.members?.[user.uid]?.avatar ? (
                  <img
                    src={bandData.members[user.uid].avatar}
                    alt="Your avatar"
                    className="w-20 h-20 rounded-full object-cover border-2 border-brand"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center border-2 border-surface-border-light">
                    <User className="w-10 h-10 text-text-subtle" />
                  </div>
                )}
                <div className="flex-1">
                  <DragDropZone
                    accept="image/*"
                    onFileSelect={(file) => updateAvatar(file)}
                    disabled={uploadingAvatar}
                    icon={User}
                    message={uploadingAvatar ? "Uploading..." : "Drag and drop profile picture here, or click to browse (JPG, PNG, GIF)"}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Display Name</label>
              <p className="text-xs text-text-subtle mb-2">Current: {bandData?.members?.[user.uid]?.displayName || user.displayName || 'Not set'}</p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  placeholder="New display name"
                  className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                />
                <button
                  onClick={updateDisplayName}
                  disabled={!newDisplayName.trim()}
                  className="w-full sm:w-auto bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 h-btn font-semibold text-sm rounded-pill"
                >
                  Update Display Name
                </button>
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Update Email</label>
              <p className="text-xs text-text-subtle mb-2">Current: {user.email}</p>
              <div className="space-y-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="New email address"
                  className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                />
                <button
                  onClick={updateUserEmail}
                  disabled={!newEmail.trim()}
                  className="w-full sm:w-auto bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 h-btn font-semibold text-sm rounded-pill"
                >
                  Update Email
                </button>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Change Password</label>
              <div className="space-y-2">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                />
                <button
                  onClick={updateUserPassword}
                  disabled={!newPassword || !confirmPassword}
                  className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 h-btn font-semibold text-sm rounded-pill"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
